import React, { useState, useEffect } from 'react';
import billingService from '../../api/billingService';
import customerService from '../../api/customerService';
import CompactPagination from '../../components/billing/CompactPagination';
import { formatDate } from '../../utils/formatters';
import { fromLocalDateTimeInputToISO } from '../../utils/dateUtils';

const NEXT_FOLLOWUP_OPTIONS = [
  { value: '1w', label: '1 Week' },
  { value: '2w', label: '2 Weeks' },
  { value: '1m', label: '1 Month' },
  { value: '3m', label: '3 Months' },
  { value: '6m', label: '6 Months' },
  { value: 'other', label: 'Other (pick a date)' }
];

const pad = (n) => String(n).padStart(2, '0');

const toLocalInputString = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;

const computePresetDate = (preset) => {
  const d = new Date();
  if (preset === '1w') d.setDate(d.getDate() + 7);
  else if (preset === '2w') d.setDate(d.getDate() + 14);
  else if (preset === '1m') d.setMonth(d.getMonth() + 1);
  else if (preset === '3m') d.setMonth(d.getMonth() + 3);
  else if (preset === '6m') d.setMonth(d.getMonth() + 6);
  return d;
};

const FeedbackList = () => {
  const [followups, setFollowups] = useState([]);
  const [members, setMembers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [status, setStatus] = useState('pending');
  const [assignTo, setAssignTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [userRole, setUserRole] = useState('owner');
  const [tableActions, setTableActions] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showCommModal, setShowCommModal] = useState(false);
  const [selectedFollowup, setSelectedFollowup] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [customerServices, setCustomerServices] = useState([]);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [completionComment, setCompletionComment] = useState('');
  const [completionFeedback, setCompletionFeedback] = useState('satisfied');
  const [completionUsage, setCompletionUsage] = useState('using');
  const [nextFollowupPreset, setNextFollowupPreset] = useState('');
  const [nextFollowupCustomDate, setNextFollowupCustomDate] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchFollowups();
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, assignTo, search]);

  const fetchSettings = async () => {
    try {
      const res = await billingService.getSettings();
      if (res.success) setSettings(res.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const replaceVars = (str, vars) => {
    if (typeof str !== 'string') return str;
    let result = str;
    Object.keys(vars).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      result = result.replace(regex, vars[key]);
    });
    return result;
  };

  useEffect(() => {
    if (!showCommModal) {
      setSelectedServiceId('');
      setCustomerServices([]);
      return;
    }
    const cid = selectedFollowup?.customer_id?._id || selectedFollowup?.customer_id;
    if (!cid) return;
    (async () => {
      try {
        const res = await billingService.getSubscriptions({ customer_id: cid, limit: 5000 });
        const list = Array.isArray(res?.data) ? res.data : [];
        setCustomerServices(list);
        const defaultSid = (() => {
          const linkedSid = selectedFollowup?.subscription_id?._id || selectedFollowup?.subscription_id;
          if (linkedSid && list.some(s => String(s._id) === String(linkedSid))) return String(linkedSid);
          return list.length > 0 ? String(list[0]._id) : '';
        })();
        setSelectedServiceId(defaultSid);
      } catch (error) {
        console.error('Error loading customer services for comm modal:', error);
        setCustomerServices([]);
        setSelectedServiceId('');
      }
    })();
  }, [showCommModal, selectedFollowup]);

  const getVariablesForService = (f, service) => {
    const cust = f?.customer || f?.subscription?.customerData || {};
    const sub = service || f?.subscription || {};
    const amount = typeof sub.amount === 'number' || typeof sub.amount === 'string' ? sub.amount : 0;
    const qty = typeof sub.quantity === 'number' || typeof sub.quantity === 'string' ? sub.quantity : 1;
    const renewal = sub.next_renewal_date || sub.expiryDate || sub.expiry_date;
    const start = sub.start_date || sub.startDate || sub.sales_date;
    return {
      customer_name: `${cust.firstname || ''} ${cust.lastname || ''}`.trim(),
      customer_phone: cust.phone || cust.phoneNumber || '',
      customer_email: cust.email || '',
      company_name: cust.company || cust.companyName || 'N/A',
      customer_id: cust._id || cust.customerId || 'N/A',
      group_id: cust.groupId || '',
      service_alias: sub.custom_alias || sub.customAlias || sub.product?.productName || sub.productName || 'Service',
      product_name: sub.productName || sub.product?.productName || sub.custom_alias || 'N/A',
      amount,
      qty,
      renewal_date: renewal ? formatDate(renewal) : 'N/A',
      start_date: start ? formatDate(start) : 'N/A',
      service_detail: sub.service_details || sub.comment || sub.serviceDetails || 'N/A'
    };
  };

  const handleSendComm = async () => {
    if (!selectedTemplate || !selectedFollowup) return;
    try {
      setSending(true);
      const selectedService = customerServices.find(s => String(s._id) === String(selectedServiceId)) || null;
      const vars = getVariablesForService(selectedFollowup, selectedService);
      const res = await billingService.sendTemplateMessage(selectedTemplate, vars);
      if (res.success) {
        alert('Communication sent successfully!');
        setShowCommModal(false);
      }
    } catch (error) {
      alert(`Failed to send: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const sessionRes = await customerService.getSession();
      if (sessionRes && sessionRes.success) {
        const userInfo = sessionRes.userInfo;
        setUserRole(userInfo.role);
        setTableActions(userInfo.permissions?.tableActions?.feedback || {});
        if (userInfo.role !== 'owner') {
          setAssignTo(userInfo.email);
        }
        const response = await customerService.getAllMembers(userInfo.id);
        if (response && response.data && response.data.ActiveMembers) {
          setMembers(response.data.ActiveMembers);
        }
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const fetchFollowups = async () => {
    try {
      setLoading(true);
      const res = await billingService.getFollowups({ page, limit, status, assignTo, search });
      if (res.success) {
        setFollowups(res.data);
        setTotal(res.total);
      }
    } catch (error) {
      console.error('Error fetching followups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteClick = (followup) => {
    setSelectedFollowup(followup);
    setCompletionComment('');
    setCompletionFeedback(followup.feedback === 'satisfied' || followup.feedback === 'disatisfied' ? followup.feedback : 'satisfied');
    setCompletionUsage(followup.usage === 'using' || followup.usage === 'not using' ? followup.usage : 'using');
    setNextFollowupPreset('');
    setNextFollowupCustomDate('');
    setShowModal(true);
  };

  const handleViewHistory = async (followup) => {
    try {
      setSelectedFollowup(followup);
      setFeedbackHistory([]);
      setShowHistoryModal(true);

      const customerId = followup.customer_id || followup.customer?._id || (typeof followup.customer_id === 'object' ? followup.customer_id?._id : null);
      const subId = followup.subscription_id;

      let res = null;
      if (customerId) {
        res = await billingService.getFollowups({ customer_id: customerId, status: 'all', limit: 5000 });
      } else if (subId) {
        res = await billingService.getFollowups({ subscription_id: subId, status: 'all', limit: 5000 });
      }

      if (res && res.success) {
        let history = Array.isArray(res.data) ? res.data : [];
        if (subId) {
          const subIdStr = typeof subId === 'object' ? String(subId._id || subId) : String(subId);
          const sameSubFirst = history.filter(h => {
            const hSub = typeof h.subscription_id === 'object' ? String(h.subscription_id._id || h.subscription_id) : String(h.subscription_id);
            return hSub === subIdStr;
          });
          const otherCustomer = history.filter(h => {
            const hSub = typeof h.subscription_id === 'object' ? String(h.subscription_id._id || h.subscription_id) : String(h.subscription_id);
            return hSub !== subIdStr;
          });
          history = [...sameSubFirst, ...otherCustomer];
        }
        setFeedbackHistory(history);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const resolveNextFollowupDate = () => {
    if (!nextFollowupPreset) return null;
    if (nextFollowupPreset === 'other') {
      return nextFollowupCustomDate ? fromLocalDateTimeInputToISO(nextFollowupCustomDate) : null;
    }
    return fromLocalDateTimeInputToISO(toLocalInputString(computePresetDate(nextFollowupPreset)));
  };

  const handleCompleteSubmit = async (e) => {
    e.preventDefault();

    const nextDateISO = resolveNextFollowupDate();
    if (nextFollowupPreset === 'other' && !nextDateISO) {
      alert('Please pick the next follow-up date.');
      return;
    }

    try {
      await billingService.updateFollowup(selectedFollowup._id, {
        status: 'completed',
        feedback: completionFeedback,
        usage: completionUsage,
        completion_comment: completionComment
      });

      // Close the current follow-up and open a fresh pending one for the same customer/service
      if (nextDateISO) {
        const customerId = selectedFollowup?.customer_id?._id || selectedFollowup?.customer_id || selectedFollowup?.customer?._id;
        const subscriptionId = selectedFollowup?.subscription_id?._id || selectedFollowup?.subscription_id || selectedFollowup?.subscription?._id;
        const payload = {
          customer_id: customerId,
          subscription_id: subscriptionId,
          comment: '',
          schedule_followup: true,
          followup_date: nextDateISO,
          status: 'pending'
        };
        if (userRole !== 'owner' && assignTo) {
          payload.assignTo = assignTo;
        }
        await billingService.createFollowup(payload);
      }

      setShowModal(false);
      fetchFollowups();
      alert(nextDateISO ? 'Follow-up completed and next follow-up scheduled!' : 'Follow-up marked as completed!');
    } catch (error) {
      alert(error.message);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-700 mb-1">Follow-ups & Feedback</h4>
          <p className="text-muted small mb-0">Manage customer feedback and scheduled follow-ups.</p>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-header border-0 bg-transparent pt-4 px-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div className="nav nav-pills custom-pills">
              <button 
                className={`nav-link px-4 py-2 me-2 fw-600 ${status === 'pending' ? 'active shadow-sm' : 'text-muted bg-light'}`} 
                onClick={() => { setStatus('pending'); setPage(1); }}
              >
                Pending Follow-ups
              </button>
              <button 
                className={`nav-link px-4 py-2 me-2 fw-600 ${status === 'completed' ? 'active shadow-sm' : 'text-muted bg-light'}`} 
                onClick={() => { setStatus('completed'); setPage(1); }}
              >
                Completed
              </button>
              <button 
                className={`nav-link px-4 py-2 fw-600 ${status === 'all' ? 'active shadow-sm' : 'text-muted bg-light'}`} 
                onClick={() => { setStatus('all'); setPage(1); }}
              >
                All Records
              </button>
            </div>
            <div className="d-flex align-items-center gap-3">
              <form className="d-flex align-items-center" onSubmit={handleSearchSubmit}>
                <div className="input-group" style={{ minWidth: '260px' }}>
                  <span className="input-group-text bg-light border-0"><i className="bi bi-search"></i></span>
                  <input
                    type="text"
                    className="form-control bg-light border-0"
                    placeholder="Search customer or product..."
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                  />
                  {search ? (
                    <button type="button" className="btn btn-light border-0" onClick={handleSearchClear} title="Clear">
                      <i className="bi bi-x-lg"></i>
                    </button>
                  ) : (
                    <button type="submit" className="btn btn-light border-0" title="Search">
                      <i className="bi bi-arrow-right"></i>
                    </button>
                  )}
                </div>
              </form>
              <div style={{ minWidth: '200px' }}>
                <select 
                  className="form-select bg-light border-0" 
                  value={assignTo} 
                  onChange={e => { setAssignTo(e.target.value); setPage(1); }}
                  disabled={userRole !== 'owner'}
                >
                  <option value="">All Members</option>
                  {members.map(member => (
                    <option key={member.username} value={member.username}>{member.username}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className="table-responsive mt-3">
          <table className="table table-hover align-middle">
            <thead className="bg-light">
              <tr>
                <th>Customer / Service</th>
                <th>Feedback / Usage</th>
                <th>Initial Comment</th>
                <th>Follow-up Date</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-5">Loading...</td></tr>
              ) : followups.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-5">No follow-ups found.</td></tr>
              ) : (
                followups.map((f) => (
                  <tr key={f._id}>
                    <td>
                      <div className="fw-600">{f.customer?.firstname || f.subscription?.customerData?.firstname} {f.customer?.lastname || f.subscription?.customerData?.lastname}</div>
                      <div className="text-xs text-primary">{f.customer?.company || f.subscription?.customerData?.company || 'N/A'}</div>
                      <div className="text-xs text-muted">{f.customer?.phone || f.subscription?.customerData?.phone || 'N/A'}</div>
                      <div className="text-xs fw-600 mt-1"><i className="bi bi-box-seam me-1"></i>{f.subscription?.productData?.productName || f.subscription?.product?.productName || f.subscription?.custom_alias || 'N/A'}</div>
                    </td>
                    <td>
                      <div className={`badge badge-soft-${f.feedback === 'satisfied' ? 'success' : 'danger'} me-1`}>{f.feedback?.toUpperCase()}</div>
                      <div className={`badge badge-soft-${f.usage === 'using' ? 'info' : 'secondary'}`}>{f.usage?.toUpperCase()}</div>
                    </td>
                    <td style={{ maxWidth: '200px' }}>
                      <div className="text-xs text-truncate" title={f.comment}>{f.comment || '-'}</div>
                    </td>
                    <td>
                      <div className="fw-600 text-danger">{f.schedule_followup ? formatDate(f.followup_date) : 'No Schedule'}</div>
                    </td>
                    <td>
                      <span className={`badge badge-soft-${f.status === 'completed' ? 'success' : 'warning'}`}>
                        {f.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="text-end">
                      <div className="d-flex justify-content-end gap-2">
                        {f.status === 'pending' && (userRole === 'owner' || tableActions.followup !== false) && (
                          <button className="btn btn-sm btn-primary" onClick={() => handleCompleteClick(f)}>
                            Mark Complete
                          </button>
                        )}
                        {(userRole === 'owner' || tableActions.view !== false) && (
                          <button className="btn btn-sm btn-light text-primary" onClick={() => handleViewHistory(f)} title="View History">
                            <i className="bi bi-clock-history me-1"></i> History
                          </button>
                        )}
                        {settings?.communication?.enabled && (userRole === 'owner' || tableActions.followup !== false) && (
                          <button className="btn btn-sm btn-light text-success" onClick={() => { setSelectedFollowup(f); setShowCommModal(true); }} title="Send Communication">
                            <i className="bi bi-chat-left-dots me-1"></i> Comm
                          </button>
                        )}
                        {f.status === 'completed' && f.completion_comment && (userRole === 'owner' || tableActions.view !== false) && (
                          <button className="btn btn-sm btn-light text-muted" title={f.completion_comment}>
                            <i className="bi bi-info-circle"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {total > limit && (
          <div className="card-footer bg-transparent border-0 py-4 px-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="text-muted small">Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} results</div>
              <CompactPagination
                page={page}
                total={total}
                limit={limit}
                onPageChange={setPage}
              />
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content border-0">
              <div className="modal-header border-0">
                <h5 className="modal-title fw-bold">Complete Follow-up</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleCompleteSubmit}>
                <div className="modal-body p-4">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">FEEDBACK</label>
                      <select className="form-select" value={completionFeedback} onChange={e => setCompletionFeedback(e.target.value)} required>
                        <option value="satisfied">Satisfied</option>
                        <option value="disatisfied">Disatisfied</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">USAGE</label>
                      <select className="form-select" value={completionUsage} onChange={e => setCompletionUsage(e.target.value)} required>
                        <option value="using">Using</option>
                        <option value="not using">Not Using</option>
                      </select>
                    </div>
                  </div>
                  <div className="mb-3 mt-3">
                    <label className="form-label text-muted small fw-600">COMPLETION COMMENT / NOTES</label>
                    <textarea 
                      className="form-control" 
                      rows="4" 
                      value={completionComment} 
                      onChange={e => setCompletionComment(e.target.value)}
                      placeholder="What was the outcome of this follow-up?"
                    ></textarea>
                  </div>

                  <div className="p-3 bg-light rounded-3">
                    <label className="form-label text-muted small fw-600 mb-2">
                      NEXT FOLLOW-UP DATE <span className="fw-400">(Optional)</span>
                    </label>
                    <select
                      className="form-select mb-2"
                      value={nextFollowupPreset}
                      onChange={e => setNextFollowupPreset(e.target.value)}
                    >
                      <option value="">Do not schedule</option>
                      {NEXT_FOLLOWUP_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {nextFollowupPreset === 'other' && (
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={nextFollowupCustomDate}
                        onChange={e => setNextFollowupCustomDate(e.target.value)}
                        required
                      />
                    )}
                    <div className="text-xs text-muted mt-2">
                      <i className="bi bi-info-circle me-1"></i>
                      Choosing a date closes this follow-up and creates a new pending follow-up for the same customer and service.
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0 p-4">
                  <button type="button" className="btn btn-light px-4" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary px-4">Mark as Completed</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showHistoryModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content border-0">
              <div className="modal-header border-0">
                <h5 className="modal-title fw-bold">Feedback History</h5>
                <button type="button" className="btn-close" onClick={() => setShowHistoryModal(false)}></button>
              </div>
              <div className="modal-body p-4 bg-light">
                <div className="mb-4">
                  <h6 className="fw-700 mb-1">{selectedFollowup?.subscription?.customerData?.firstname} {selectedFollowup?.subscription?.customerData?.lastname}</h6>
                  <p className="text-xs text-muted mb-0">{selectedFollowup?.subscription?.custom_alias || 'General Subscription'}</p>
                </div>
                <div className="overflow-auto" style={{ maxHeight: '500px' }}>
                  {feedbackHistory.length > 0 ? (
                    feedbackHistory.slice().reverse().map((h, i) => (
                      <div key={i} className="card border-0 shadow-sm mb-3 rounded-3">
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between mb-2">
                            <div>
                              <span className={`badge badge-soft-${h.feedback === 'satisfied' ? 'success' : 'danger'} text-xs me-2`}>
                                {h.feedback?.toUpperCase()}
                              </span>
                              <span className={`badge badge-soft-${h.status === 'completed' ? 'success' : 'warning'} text-xs`}>
                                {h.status?.toUpperCase()}
                              </span>
                            </div>
                            <span className="text-xs text-muted">{formatDate(h.created_at || h.date)}</span>
                          </div>
                          <div className="text-xs fw-600 mb-1">Usage: <span className="text-primary">{h.usage}</span></div>
                          <div className="text-xs text-dark italic mb-2">"{h.comment || 'No comment provided'}"</div>
                          {h.status === 'completed' && h.completion_comment && (
                            <div className="mt-2 p-2 bg-light rounded text-xs">
                              <strong>Completion Note:</strong> {h.completion_comment}
                            </div>
                          )}
                          {h.schedule_followup && (
                            <div className="mt-2 pt-2 border-top text-xs text-muted">
                              <i className="bi bi-calendar-event me-1"></i>
                              Follow-up Date: <span className="fw-600 text-info">{formatDate(h.followup_date)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-5 text-muted small">
                      <i className="bi bi-chat-left-text d-block h4 mb-2"></i>
                      No history records found.
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer border-0 p-4">
                <button type="button" className="btn btn-light px-4" onClick={() => setShowHistoryModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCommModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content border-0">
              <div className="modal-header border-0">
                <h5 className="modal-title fw-bold">Send Communication</h5>
                <button type="button" className="btn-close" onClick={() => setShowCommModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="mb-3">
                  <label className="form-label text-muted small fw-600">SERVICE</label>
                  {customerServices.length === 0 ? (
                    <div className="text-sm text-muted border rounded p-2 bg-light">
                      <i className="bi bi-exclamation-triangle me-1 text-warning"></i>
                      No services for customer
                    </div>
                  ) : (
                    <select
                      className="form-select"
                      value={selectedServiceId}
                      onChange={e => setSelectedServiceId(e.target.value)}
                    >
                      {customerServices.map(s => {
                        const label = [
                          s.productName || s.product?.productName || s.custom_alias || 'Service',
                          s.custom_alias ? `[${s.custom_alias}]` : null,
                          s.amount ? `· ₹${s.amount}` : null,
                          s.next_renewal_date || s.expiry_date ? `(${formatDate(s.next_renewal_date || s.expiry_date)})` : null
                        ].filter(Boolean).join(' ');
                        return (
                          <option key={String(s._id)} value={String(s._id)}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label text-muted small fw-600">SELECT TEMPLATE</label>
                  <select 
                     className="form-select" 
                     value={selectedTemplate?._id || selectedTemplate?.id || ''} 
                     onChange={e => {
                       const t = settings?.communication?.templates?.find(t => (t._id || String(t.id)) === e.target.value);
                       setSelectedTemplate(t);
                     }}
                   >
                     <option value="">Select a template...</option>
                     {settings?.communication?.templates?.map(t => (
                       <option key={t._id || t.id} value={t._id || t.id}>{t.name}</option>
                     ))}
                   </select>
                </div>

                {selectedTemplate && (
                  <div className="card border-0 bg-light p-3">
                    <div className="mb-3">
                      <label className="text-xs fw-700 text-muted d-block mb-1">PREVIEW (REPLACED VARIABLES)</label>
                      {(() => {
                        const selectedService = customerServices.find(s => String(s._id) === String(selectedServiceId)) || null;
                        const vars = getVariablesForService(selectedFollowup, selectedService);
                        return (
                          <div className="bg-white p-3 rounded border small">
                            <div className="mb-2"><strong>URL:</strong> {replaceVars(selectedTemplate.endpoint, vars)}</div>
                            <div className="mb-2"><strong>Method:</strong> {selectedTemplate.method}</div>
                            
                            {selectedTemplate.headers?.length > 0 && (
                              <div className="mb-2">
                                <strong>Headers:</strong>
                                <div className="text-xs text-muted">
                                  {selectedTemplate.headers.map((h, i) => (
                                    <div key={i}>{h.key}: {replaceVars(h.value, vars)}</div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {selectedTemplate.queryParams?.length > 0 && (
                              <div className="mb-2">
                                <strong>Query Params:</strong>
                                <div className="text-xs text-muted">
                                  {selectedTemplate.queryParams.map((p, i) => (
                                    <div key={i}>{p.key}: {replaceVars(p.value, vars)}</div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div><strong>Body:</strong></div>
                            <pre className="mb-0 mt-1 bg-light p-2 rounded" style={{ fontSize: '11px' }}>
                              {selectedTemplate.bodyType === 'json' 
                                ? replaceVars(selectedTemplate.bodyJson || selectedTemplate.body, vars)
                                : JSON.stringify(
                                    selectedTemplate.bodyFields?.reduce((acc, f) => {
                                      acc[f.key] = replaceVars(f.value, vars);
                                      return acc;
                                    }, {}), 
                                    null, 
                                    2
                                  )
                              }
                            </pre>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer border-0 p-4">
                <button type="button" className="btn btn-light px-4" onClick={() => setShowCommModal(false)}>Cancel</button>
                <button 
                  type="button" 
                  className="btn btn-success px-4" 
                  onClick={handleSendComm}
                  disabled={sending || !selectedTemplate}
                >
                  {sending ? 'Sending...' : 'Send Now'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackList;
